<!-- src/lib/components/PulseView.svelte -->
<!--
  Workspace Pulse — refinement #41. Activity digest showing event
  counts, recent events timeline, and quick stats. Lives under
  Planning section.
-->
<script lang="ts">
  import { api } from '../api.ts';
  import { timeAgo } from '../format-time.ts';
  import { agentStore } from '../stores/agents.svelte.ts';
  import { pmStore } from '../stores/pm.svelte.ts';
  import EmptyState from './EmptyState.svelte';

  interface EventRow {
    id: string;
    agent_id: string;
    event_type: string;
    payload: Record<string, unknown>;
    created_at: string;
  }

  let events = $state<EventRow[]>([]);
  let loading = $state(true);

  async function load() {
    loading = true;
    try {
      events = await api<EventRow[]>('/api/v1/events?limit=100');
    } catch { events = []; }
    finally { loading = false; }
  }

  $effect(() => { void load(); });

  // Group by event_type → count
  const typeCounts = $derived((() => {
    const map = new Map<string, number>();
    for (const e of events) {
      map.set(e.event_type, (map.get(e.event_type) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  })());

  // Recent 20 for timeline
  const recent = $derived(events.slice(0, 20));

  const running = $derived(agentStore.running.length);
  const queued = $derived(agentStore.queued.length);
  const completedToday = $derived(agentStore.completedToday);
  const completedByHuman = $derived(agentStore.completedByHuman);
  const failedToday = $derived(agentStore.failedToday);
  const cardLookup = $derived((() => {
    const lookup = new Map<string, string>();
    for (const board of Object.values(pmStore.boardDetails)) {
      for (const column of board.columns) {
        for (const card of column.cards) {
          lookup.set(card.id, card.title);
        }
      }
    }
    return lookup;
  })());

  const narrativeLine = $derived((() => {
    const running = agentStore.running.length;
    const done = agentStore.completedToday + agentStore.completedByHuman;
    const taskWord = done === 1 ? 'task' : 'tasks';
    const agentWord = running === 1 ? 'agent' : 'agents';
    if (running > 0 && done > 0) {
      return `${running} ${agentWord} running · ${done} ${taskWord} completed today`;
    }
    if (running > 0) {
      return `${running} ${agentWord} running`;
    }
    if (done > 0) {
      return `All quiet · ${done} ${taskWord} completed today`;
    }
    return 'No activity yet — dispatch an agent to get started';
  })());

  function eventLabel(type: string): string {
    return type.replace(/_/g, ' ');
  }
  function eventColor(type: string): string {
    if (type.includes('created')) return 'oklch(0.78 0.16 150)';
    if (type.includes('updated') || type.includes('moved')) return 'oklch(0.78 0.16 85)';
    if (type.includes('deleted') || type.includes('failed') || type.includes('cancelled')) return 'oklch(0.68 0.24 25)';
    if (type.includes('completed')) return 'oklch(0.78 0.16 150)';
    if (type.includes('started') || type.includes('queued')) return 'oklch(0.68 0.18 265)';
    return 'oklch(0.60 0.05 310 / 0.6)';
  }

  function resolveEventLabel(event: EventRow): string {
    const payload = event.payload as { card_id?: unknown; title?: unknown; prompt?: unknown };
    if (typeof payload.card_id === 'string') {
      const cardTitle = cardLookup.get(payload.card_id);
      if (cardTitle) return cardTitle;
    }
    if (typeof payload.title === 'string' && payload.title.trim()) {
      return payload.title.trim();
    }
    if (typeof payload.prompt === 'string' && payload.prompt.trim()) {
      const prompt = payload.prompt.trim();
      return prompt.length > 80 ? `${prompt.slice(0, 77)}...` : prompt;
    }
    return event.agent_id.slice(0, 8);
  }
</script>

{#if loading}
  <EmptyState icon="timeline" title="Loading pulse…" />
{:else}
  <div class="pulse">
    <header class="pulse-header">
      <h1>Pulse</h1>
      <p class="pulse-sub">Recent workspace activity{events.length > 0 ? ` · ${events.length} events` : ''}</p>
    </header>

    <div class="narrative-banner">
      <span class="narrative-dot" class:active={agentStore.running.length > 0}></span>
      <span class="narrative-text">{narrativeLine}</span>
    </div>

    <!-- Quick stats — always rendered, reads from agentStore not events -->
    <div class="stat-row">
      <div class="stat-card">
        <span class="stat-value">{running}</span>
        <span class="stat-label">Running agents</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">{queued}</span>
        <span class="stat-label">Queued tasks</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">{completedToday}</span>
        <span class="stat-label">Completed today</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">{failedToday}</span>
        <span class="stat-label">Failed today</span>
      </div>
    </div>

    <!-- Velocity split — agent vs human completions today -->
    <section class="pulse-card velocity-card">
      <h2>Velocity</h2>
      <div class="velocity-bar-wrap">
        <div class="velocity-bar">
          {#if completedToday > 0 || completedByHuman > 0}
            {@const total = completedToday + completedByHuman}
            {#if completedToday > 0}
              <div
                class="vel-seg vel-agent"
                style="width: {Math.round((completedToday / total) * 100)}%"
                title="Agent: {completedToday}"
              ></div>
            {/if}
            {#if completedByHuman > 0}
              <div
                class="vel-seg vel-human"
                style="width: {Math.round((completedByHuman / total) * 100)}%"
                title="Human: {completedByHuman}"
              ></div>
            {/if}
          {:else}
            <div class="vel-empty"></div>
          {/if}
        </div>
      </div>
      <p class="velocity-label">Today: <strong>{completedToday}</strong> by agents, <strong>{completedByHuman}</strong> by humans</p>
    </section>

    {#if events.length === 0}
      <EmptyState icon="timeline" title="No activity yet" subtitle="Events appear here as you create cards, run agents, and update projects." />
    {:else}
      <!-- Event type breakdown -->
      <section class="pulse-card">
        <h2>Event breakdown</h2>
        <div class="breakdown">
          {#each typeCounts as [type, count] (type)}
            <div class="breakdown-row">
              <span class="breakdown-dot" style="background: {eventColor(type)}"></span>
              <span class="breakdown-type">{eventLabel(type)}</span>
              <span class="breakdown-count">{count}</span>
            </div>
          {/each}
        </div>
      </section>

      <!-- Recent timeline -->
      <section class="pulse-card">
        <h2>Recent activity</h2>
        <div class="timeline">
          {#each recent as ev (ev.id)}
            <div class="timeline-row">
              <span class="tl-dot" style="background: {eventColor(ev.event_type)}"></span>
              <span class="tl-type">{eventLabel(ev.event_type)}</span>
              <span class="tl-label" title={resolveEventLabel(ev)}>{resolveEventLabel(ev)}</span>
              <span class="tl-id" title={ev.agent_id}>{ev.agent_id.slice(0, 8)}</span>
              <span class="tl-time">{timeAgo(ev.created_at)}</span>
            </div>
          {/each}
        </div>
      </section>
    {/if}
  </div>
{/if}

<style>
  .pulse {
    flex: 1; overflow-y: auto; padding: 2rem 2.5rem;
    display: flex; flex-direction: column; gap: 1.5rem;
    max-width: 820px; color: oklch(0.85 0.05 310 / 0.85);
  }
  .pulse-header h1 { margin: 0; font-size: 1.5rem; font-weight: 600; color: oklch(0.94 0.08 310); }
  .pulse-sub { margin: 0.2rem 0 0; font-size: 0.78rem; color: oklch(0.55 0.04 310 / 0.60); }

  .narrative-banner {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.875rem 1.25rem;
    background: oklch(0.12 0.02 275);
    border: 1px solid oklch(0.22 0.03 275);
    border-radius: 8px;
    font-size: 0.875rem;
    color: oklch(0.78 0.04 275);
  }

  .narrative-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: oklch(0.50 0.04 275);
    flex-shrink: 0;
    transition: background 0.3s ease;
  }

  .narrative-dot.active {
    background: oklch(0.72 0.28 310);
    box-shadow: 0 0 6px oklch(0.72 0.28 310 / 0.5);
    animation: pulse-dot 2s ease-in-out infinite;
  }

  .narrative-text {
    min-width: 0;
  }

  .stat-row { display: flex; gap: 0.75rem; }
  .stat-card {
    flex: 1; background: oklch(0.11 0.02 275); border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 6px; padding: 0.85rem 1rem; display: flex; flex-direction: column; gap: 0.2rem;
  }
  .stat-value { font-size: 1.3rem; font-weight: 700; color: oklch(0.94 0.08 310); font-family: 'JetBrains Mono', monospace; }
  .stat-label { font-size: 0.68rem; color: oklch(0.55 0.04 310 / 0.55); text-transform: uppercase; letter-spacing: 0.06em; }

  .pulse-card {
    background: oklch(0.11 0.02 275); border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 6px; padding: 1rem 1.25rem;
  }
  .pulse-card h2 { margin: 0 0 0.6rem; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: oklch(0.65 0.06 310 / 0.65); }

  .breakdown { display: flex; flex-direction: column; gap: 0.3rem; }
  .breakdown-row { display: flex; align-items: center; gap: 0.5rem; font-size: 0.78rem; }
  .breakdown-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .breakdown-type { flex: 1; color: oklch(0.82 0.05 310 / 0.80); text-transform: capitalize; }
  .breakdown-count { font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; color: oklch(0.60 0.04 310 / 0.60); }

  .timeline { display: flex; flex-direction: column; gap: 0.35rem; }
  .timeline-row { display: flex; align-items: center; gap: 0.55rem; font-size: 0.75rem; }
  .tl-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
  .tl-type { color: oklch(0.82 0.05 310); text-transform: capitalize; min-width: 130px; }
  .tl-label {
    flex: 1;
    min-width: 0;
    color: oklch(0.88 0.05 310 / 0.88);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .tl-id { font-family: 'JetBrains Mono', monospace; font-size: 0.65rem; color: oklch(0.55 0.04 310 / 0.50); }
  .tl-time { font-family: 'JetBrains Mono', monospace; font-size: 0.65rem; color: oklch(0.55 0.04 310 / 0.50); flex-shrink: 0; }

  .velocity-card { display: flex; flex-direction: column; gap: 0.5rem; }
  .velocity-bar-wrap { width: 100%; }
  .velocity-bar {
    height: 8px;
    border-radius: 4px;
    background: oklch(0.16 0.02 275);
    overflow: hidden;
    display: flex;
  }
  .vel-seg { height: 100%; transition: width 400ms ease; }
  .vel-agent { background: oklch(0.72 0.28 310); }
  .vel-human { background: oklch(0.65 0.20 250); }
  .vel-empty { width: 100%; height: 100%; background: oklch(0.16 0.02 275); }
  .velocity-label {
    margin: 0;
    font-size: 0.72rem;
    color: oklch(0.60 0.04 310 / 0.70);
  }
  .velocity-label strong {
    font-family: 'JetBrains Mono', monospace;
    color: oklch(0.82 0.06 310 / 0.90);
    font-weight: 600;
  }

  @keyframes pulse-dot {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
</style>
